import React from 'react';
import { Shield, Lock, Eye, FileCheck, Users, Server } from 'lucide-react';

const ComplianceSection = ({ isDark }) => {
  const compliance = [
    {
      icon: Shield,
      title: 'HIPAA Compliant',
      description: 'Full compliance with US healthcare data privacy regulations'
    },
    {
      icon: Lock,
      title: 'GDPR Ready',
      description: 'European data protection standards fully implemented'
    },
    {
      icon: FileCheck,
      title: 'NHS / UK Ready',
      description: 'DCB0129/DCB0160 clinical safety frameworks'
    },
    {
      icon: Eye,
      title: 'Explainable AI',
      description: 'No black box decisions. Full transparency on AI reasoning'
    },
    {
      icon: Users,
      title: 'Human-in-the-Loop',
      description: 'Clinician oversight at every critical decision point'
    },
    {
      icon: Server,
      title: 'Audit Trails',
      description: 'Complete logging for regulatory compliance'
    }
  ];

  return (
    <section 
      id="compliance" 
      className={`section-padding ${isDark ? 'bg-slate-900' : 'bg-white'}`}
      data-testid="compliance-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 ${
            isDark ? 'bg-emerald-900/50 text-emerald-400' : 'bg-emerald-100 text-emerald-700'
          }`}>
            Trust & Safety
          </span>
          <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 ${
            isDark ? 'text-white' : 'text-slate-900'
          }`}>
            Built for Healthcare Compliance
          </h2>
          <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
            Enterprise-grade security and compliance for the healthcare industry's most demanding requirements.
          </p>
        </div>

        {/* Compliance grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {compliance.map((item, index) => (
            <div 
              key={index}
              className={`p-6 rounded-xl border transition-colors ${
                isDark 
                  ? 'bg-slate-800 border-slate-700 hover:border-emerald-600' 
                  : 'bg-slate-50 border-slate-200 hover:border-emerald-400'
              }`}
              data-testid={`compliance-${index}`}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                isDark ? 'bg-emerald-900/50' : 'bg-emerald-100'
              }`}>
                <item.icon className="w-6 h-6 text-emerald-500" />
              </div>
              <h3 className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {item.title}
              </h3>
              <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                {item.description}
              </p>
            </div>
          ))}
        </div>

        {/* Trust strip */}
        <div className={`rounded-2xl p-8 text-center ${isDark ? 'bg-slate-800' : 'bg-slate-900'}`}>
          <p className="text-slate-400 mb-4">Works with leading EMRs and healthcare standards</p>
          <div className="flex flex-wrap justify-center items-center gap-8">
            <span className="text-white font-semibold text-lg">HIPAA</span>
            <span className="w-1 h-6 bg-slate-600" />
            <span className="text-white font-semibold text-lg">GDPR</span>
            <span className="w-1 h-6 bg-slate-600" />
            <span className="text-white font-semibold text-lg">NHS Ready</span>
            <span className="w-1 h-6 bg-slate-600" />
            <span className="text-white font-semibold text-lg">SOC 2</span>
            <span className="w-1 h-6 bg-slate-600" />
            <span className="text-white font-semibold text-lg">ISO 27001</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComplianceSection;
